import { Star, StarHalf } from 'lucide-react';
import * as React from 'react';
import { cn } from '../lib/cn';

export interface StarRatingProps {
  value: number | null;
  onChange?: (value: number | null) => void;
  allowHalf?: boolean;
  size?: 'sm' | 'md' | 'lg';
  label?: string;
  className?: string;
  readOnly?: boolean;
}

const SIZES = { sm: 'size-4', md: 'size-6', lg: 'size-9' } as const;

/** Accessible 1–5 star input with optional half stars (click left half of a star). */
export function StarRating({
  value,
  onChange,
  allowHalf = true,
  size = 'md',
  label = 'Rating',
  className,
  readOnly,
}: StarRatingProps) {
  const [hover, setHover] = React.useState<number | null>(null);
  const shown = hover ?? value ?? 0;
  const interactive = !!onChange && !readOnly;

  const pick = (event: React.MouseEvent<HTMLButtonElement>, star: number) => {
    if (!allowHalf) return star;
    const rect = event.currentTarget.getBoundingClientRect();
    const half = event.clientX - rect.left < rect.width / 2;
    return half ? star - 0.5 : star;
  };

  return (
    <div
      className={cn('inline-flex items-center gap-0.5', className)}
      role={interactive ? 'radiogroup' : 'img'}
      aria-label={interactive ? label : `${label}: ${value ?? 0} / 5`}
    >
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = shown >= star;
        const half = !filled && shown >= star - 0.5;
        const icon = half ? (
          <StarHalf className={cn(SIZES[size], 'fill-current text-warning')} />
        ) : (
          <Star
            className={cn(
              SIZES[size],
              filled ? 'fill-current text-warning' : 'text-muted-foreground',
            )}
          />
        );
        if (!interactive) return <span key={star}>{icon}</span>;
        return (
          <button
            key={star}
            type="button"
            role="radio"
            aria-checked={value !== null && Math.ceil(value) === star}
            aria-label={`${star} ${label}`}
            className="touch-target inline-flex items-center justify-center rounded-md p-0.5 hover:bg-accent"
            onMouseMove={(e) => setHover(pick(e, star))}
            onMouseLeave={() => setHover(null)}
            onClick={(e) => {
              const next = pick(e, star);
              onChange?.(value === next ? null : next);
            }}
          >
            {icon}
          </button>
        );
      })}
    </div>
  );
}
